import { useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Play } from 'lucide-react';
import CheckoutAlert from '../CheckoutAlert';
import { ChoiceGroup, buttonClass } from '../CheckoutControls';
import {
  ACCELERATOR_COHORTS,
  ACCELERATOR_FORMATS,
  ACCELERATOR_CENTS,
  formatCents,
} from '@/lib/checkout/pricing';
import { validateAcceleratorStep } from '@/lib/checkout/validation';
import type { AcceleratorDraft, FieldErrors } from '@/lib/checkout/types';

interface AcceleratorStepProps {
  value: AcceleratorDraft;
  onChange: (patch: Partial<AcceleratorDraft>) => void;
  onNext: () => void;
  onBack: () => void;
  setupFeeCents: number;
}

/** Step 3 — optional Accelerator course add-on. Body copy is hard-coded for MVP. */
export default function AcceleratorStep({
  value,
  onChange,
  onNext,
  onBack,
  setupFeeCents,
}: AcceleratorStepProps) {
  const [attemptedAdd, setAttemptedAdd] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);

  // Errors are derived once they've tried to add, so they clear as choices are made.
  const errors: FieldErrors = attemptedAdd
    ? validateAcceleratorStep({ ...value, added: true })
    : {};

  const toggleCohort = (cohortId: string) => {
    const selected = value.cohortIds.includes(cohortId)
      ? value.cohortIds.filter((id) => id !== cohortId)
      : [...value.cohortIds, cohortId];
    onChange({ cohortIds: selected });
  };

  /** Shared by Add and Continue: an added add-on always needs a date and format. */
  const addOrContinue = () => {
    setAttemptedAdd(true);

    if (Object.keys(validateAcceleratorStep({ ...value, added: true })).length > 0) {
      requestAnimationFrame(() => summaryRef.current?.focus());
      return;
    }

    onChange({ added: true });
    onNext();
  };

  const skip = () => {
    setAttemptedAdd(false);
    onChange({ added: false, cohortIds: [], format: '' });
    onNext();
  };

  const remove = () => {
    setAttemptedAdd(false);
    onChange({ added: false, cohortIds: [], format: '' });
  };

  return (
    <>
      <h2 className="m-0 mb-4 text-xl font-bold text-[#4b0a5b] sm:text-[21px]">
        Add the Accelerator Course?
      </h2>

      <div
        role="img"
        aria-label="Accelerator promotional video — WorldVia’s 5-day immersive experience"
        className="mb-4 flex h-[200px] flex-col items-center justify-center gap-2.5 rounded-2xl border border-[#ece6f0] bg-[repeating-linear-gradient(135deg,#efe9f0,#efe9f0_10px,#f6f2f7_10px,#f6f2f7_20px)]"
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-[#ff7540] shadow-[0_6px_18px_rgba(61,23,81,0.14)]">
          <Play aria-hidden="true" className="h-5 w-5" fill="currentColor" />
        </span>
        <span className="text-[10.5px] font-medium text-[#7a6a8c]">
          Accelerator — WorldVia’s 5-day immersive experience
        </span>
      </div>

      <p className="mb-4 text-[13.5px] leading-relaxed text-[#4a3a5c]">
        Normally getting started includes a {formatCents(setupFeeCents).replace(' USD', '')}{' '}
        activation fee plus the {formatCents(ACCELERATOR_CENTS).replace(' USD', '')} Accelerator. Add
        it today and we’ll waive the activation fee, so you only pay{' '}
        {formatCents(ACCELERATOR_CENTS).replace(' USD', '')} at checkout plus your monthly
        subscription.
      </p>

      {value.added ? (
        <CheckoutAlert
          tone="success"
          title="Accelerator added to your checkout"
          compact
          actions={
            <button type="button" onClick={remove} className={buttonClass('smOutline')}>
              Remove from checkout
            </button>
          }
        >
          Your activation fee is waived and the Accelerator has been added to the order summary.
        </CheckoutAlert>
      ) : null}

      <fieldset className="mb-4" aria-describedby={errors.cohortIds ? 'cohort-error' : undefined}>
        <legend className="mb-1 text-xs font-semibold text-[#3d1751]">
          Choose your date(s)
          <span className="ml-1 text-[#ff7540]" aria-hidden="true">
            *
          </span>
        </legend>
        <p className="mb-2 text-[11px] text-[#7a6a8c]">Select every cohort that works for you.</p>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          {ACCELERATOR_COHORTS.map((cohort) => {
            const selected = value.cohortIds.includes(cohort.id);
            return (
              <div key={cohort.id} className="relative">
                <input
                  type="checkbox"
                  id={`cohort-${cohort.id}`}
                  checked={selected}
                  onChange={() => toggleCohort(cohort.id)}
                  className="peer absolute h-px w-px opacity-0"
                />
                <label
                  htmlFor={`cohort-${cohort.id}`}
                  className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-5 py-2 text-[12.5px] font-medium peer-focus-visible:ring-2 peer-focus-visible:ring-[#b82f74] peer-focus-visible:ring-offset-2 ${
                    selected
                      ? 'border-[#ff7540] bg-[#fff4ee] font-semibold text-[#c2410c]'
                      : 'border-[#d9d3e4] bg-white text-[#5c4a6e] hover:bg-[#faf7fb]'
                  }`}
                >
                  {selected ? <CheckCircle2 aria-hidden="true" className="h-3.5 w-3.5" /> : null}
                  {cohort.label}
                </label>
              </div>
            );
          })}
        </div>
        {errors.cohortIds ? (
          <p
            id="cohort-error"
            className="mt-1.5 flex items-center gap-1 text-[11.5px] font-medium text-[#dc2626]"
          >
            <AlertCircle aria-hidden="true" className="h-3.5 w-3.5 flex-none" />
            {errors.cohortIds}
          </p>
        ) : null}
      </fieldset>

      <ChoiceGroup
        legend="Format"
        name="accelerator-format"
        required
        value={value.format}
        error={errors.format}
        options={ACCELERATOR_FORMATS.map((format) => ({ value: format.id, label: format.label }))}
        onChange={(next) => onChange({ format: next as AcceleratorDraft['format'] })}
      />

      {Object.keys(errors).length > 0 ? (
        <div ref={summaryRef} tabIndex={-1}>
          <CheckoutAlert tone="error" title="Choose your date and format to add the Accelerator." compact>
            Or skip the Accelerator and continue — you can add it later.
          </CheckoutAlert>
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" onClick={onBack} className={buttonClass('outline')}>
          Back
        </button>
        {value.added ? (
          <button type="button" onClick={addOrContinue} className={buttonClass('primary')}>
            Continue
          </button>
        ) : (
          <>
            <button type="button" onClick={skip} className={buttonClass('ghost')}>
              Skip and continue
            </button>
            <button type="button" onClick={addOrContinue} className={buttonClass('primary')}>
              Add to checkout — {formatCents(ACCELERATOR_CENTS).replace(' USD', '')}
            </button>
          </>
        )}
      </div>
    </>
  );
}
